import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { map } from 'lodash';
import { Checkbox, Switch, TimeSelectorRow } from 'sharedComponents';
import { getIcon } from 'sharedComponents/GeneralDialog/helpers';
import { popupTypes, DAY_IN_MS } from 'helpers/constants';
import { formatStartDate, formatEndDate } from 'helpers/dateHelper';
import { getNotificationGroupColumns, updateMutedGroupList, updateBulkMutedGroupList } from './helpers';
import './style.scss';

const EditMutedAssetComponent = ({
    notificationGroups,
    disabledForPeriod,
    setDisabledForPeriod,
    period,
    setPeriod,
    mutedGroups,
    setMutedGroups,
    muteAssets,
    bulkMutedGroups,
    setBulkMutedgroups,
    setShowMute,
    isGeneralMute = false,
    isBulkEdit = false,
    mutedNotificationList
}) => {
    const { t: translate } = useTranslation();
    const headerIcon = getIcon(popupTypes.INFO);
    const [isSelectAll, setIsSelectAll] = useState(false);

    const notificationCodes = useMemo(() => {
        return mutedNotificationList?.length > 0
            ? new Set(mutedNotificationList.map(list => list?.notificationGroupCode))
            : new Set();
    }, [mutedNotificationList]);

    // Set bulk muted groups when notificationGroups change
    useEffect(() => {
        if (setBulkMutedgroups) {
            setBulkMutedgroups(updateBulkMutedGroupList(notificationGroups));
        }
    }, [notificationGroups, setBulkMutedgroups]);

    // Reset disabledForPeriod when bulk editing
    useEffect(() => {
        if (isBulkEdit) {
            setDisabledForPeriod(false);
        }
    }, [isBulkEdit, setDisabledForPeriod]);

    // Fix: To avoid setState during render warning
    useEffect(() => {
        if (isGeneralMute) {
            setShowMute(bulkMutedGroups?.some(group => group?.muted));
        }
    }, [bulkMutedGroups, isGeneralMute, setShowMute]);

    const filteredbulkMutedGroups = useMemo(() => {
        if (isBulkEdit || muteAssets) {
            return bulkMutedGroups?.filter(group => notificationCodes?.has(group?.notificationGroupCode));
        }
        return [];
    }, [bulkMutedGroups, isBulkEdit, muteAssets, notificationCodes]);

    useEffect(() => {
        const displayedGroups = isBulkEdit || muteAssets ? filteredbulkMutedGroups : mutedGroups;
        const allMuted = displayedGroups?.length > 0 && displayedGroups.every(group => group.muted);
        setIsSelectAll(allMuted);
    }, [mutedGroups, filteredbulkMutedGroups, isBulkEdit, muteAssets]);

    // Toggle select all
    const toggleSelectAll = () => {
        const updatedGroups = notificationGroups.map(group => ({
            ...group,
            muted: !isSelectAll
        }));
        const filteredUpdatedGroups = updatedGroups?.filter(group => notificationCodes.has(group?.notificationGroupCode));
        const muteGroupCode = mutedGroups?.map(group => group?.notificationGroupCode);
        const filteredNonBulkNotification = updatedGroups?.filter(group => muteGroupCode?.includes(group?.notificationGroupCode));
        if (isBulkEdit || muteAssets) {
            setBulkMutedgroups(filteredUpdatedGroups);
        } else {
            setMutedGroups(filteredNonBulkNotification);
        }
        setIsSelectAll(!isSelectAll);
    };

    // Toggle individual notification group mute
    const toggleGroupMute = (notificationGroup) => {
        if (isBulkEdit || muteAssets) {
            setBulkMutedgroups(updateMutedGroupList(bulkMutedGroups, notificationGroup));
        } else {
            setMutedGroups(updateMutedGroupList(mutedGroups, notificationGroup));
        }
    };

    // Render each notification group with checkbox
    const renderNotificationGroupWithCheckbox = (notificationGroup) =>
        <div className='mute-notification-group' key={notificationGroup?.notificationGroupCode}>
            <Checkbox
                checked={notificationGroup.muted !== null ? notificationGroup.muted : true}
                onToggle={() => toggleGroupMute(notificationGroup)}
                width={'22px'}
                height={'22px'}
            />
            <button
                className='group-name'
                onClick={() => toggleGroupMute(notificationGroup)}
                type='button'>
                {translate(notificationGroup?.notificationGroupNameTextKey) || notificationGroup?.notificationGroupName}
            </button>
        </div>
        ;

    // Render all notification groups
    const renderNotificationGroups = () => {
        const columns = isBulkEdit || muteAssets
            ? getNotificationGroupColumns(filteredbulkMutedGroups)
            : getNotificationGroupColumns(mutedGroups);

        return map(columns, (column) =>
            <div className='column' key={column.id}>
                {map(column.columnData, renderNotificationGroupWithCheckbox)}
            </div>
        );
    };

    return (
        <div className='edit-muted-asset-container'>
            <div className='bulk-edit-info-message'>
                <span className='icon'>{headerIcon}</span>
                <span className='title'>{translate('ABB.Powertrain.Frontend.mutedAssetMuteSelectionInfoMessage')}</span>
            </div>
            <div className='notification-group-selection'>
                <div className='section-title'>
                    <div className='title-text'>
                        {translate('ABB.Powertrain.Frontend.mutedAssetsSelectedNotificationGroups')}
                    </div>
                    <div className='select-all'>
                        <button
                            type='button'
                            className='select-all__link'
                            onClick={toggleSelectAll}
                            aria-pressed={isSelectAll}
                            aria-label={isSelectAll ? translate('ABB.Powertrain.Frontend.mutedAssetUnselectAll') : translate('ABB.Powertrain.Frontend.mutedAssetSelectAll')}
                        >
                            {isSelectAll
                                ? translate('ABB.Powertrain.Frontend.mutedAssetUnselectAll')
                                : translate('ABB.Powertrain.Frontend.mutedAssetSelectAll')}
                        </button>
                    </div>
                </div>
                <div className='notification-groups-list'>{renderNotificationGroups()}</div>
            </div>
            <div className='period-selection'>
                <div className='section-title'>
                    <Switch
                        onChange={() => {
                            setDisabledForPeriod(!disabledForPeriod);
                            if (!disabledForPeriod) {
                                const today = formatStartDate(new Date());
                                const toDate = formatEndDate(new Date(today.getTime() + 6 * DAY_IN_MS));
                                setPeriod({ from: today, to: toDate });
                            } else {
                                setPeriod(null);
                            }
                        }}
                        checked={disabledForPeriod}
                    />
                    <div className='title-text'>
                        {translate('ABB.Powertrain.Frontend.mutedAssetsSelectedPeriod')}
                    </div>
                </div>
                {disabledForPeriod &&
                    <div className='mute-period'>
                        <TimeSelectorRow
                            fromDate={period?.from}
                            toDate={period?.to}
                            handleSelectDate={(from, to) => setPeriod({ from, to })}
                            maxDaysBack={0}
                            numberOfMonths={2}
                            withPredefinedSelection={false}
                            disableFuture={false}
                        />
                    </div>
                }
            </div>
        </div>
    );
};

EditMutedAssetComponent.propTypes = {
    disabledForPeriod: PropTypes.bool,
    setDisabledForPeriod: PropTypes.func,
    period: PropTypes.object,
    setPeriod: PropTypes.func,
    mutedGroups: PropTypes.array,
    setMutedGroups: PropTypes.func,
    notificationGroups: PropTypes.array,
    setShowMute: PropTypes.func,
    isGeneralMute: PropTypes.bool,
    isBulkEdit: PropTypes.bool,
    muteAssets: PropTypes.bool,
    bulkMutedGroups: PropTypes.array,
    setBulkMutedgroups: PropTypes.func
};

export default EditMutedAssetComponent;
